import { authFetch } from "@/shared/lib/auth-fetch"
import type { ClaimedByReportFilters } from "../lib/claimed-by-workscope-status"

export type PartnerReportRequest = {
  regionId: number
  organizationName: string
  filters: ClaimedByReportFilters
}

export type EmailPartnerReportRequest = PartnerReportRequest & {
  recipients: string[]
  message: string
}

const WORK_SCOPE_SLUGS: Record<string, string> = {
  "Tree": "tree-work",
  "Exterior Debris": "exterior-debris-removal",
  "Heavy Equipment": "heavy-equipment-demo",
  "Roof Tarp": "tarp-roof",
  "Roofing": "install-roofing",
  "Muck & Gut": "muck-and-gut-interior",
  "Mold Remediation": "mold-remediation",
  "Rebuild Prep": "rebuild-prep",
  "Rough Carpentry": "rough-carpentry",
  "Electrical": "electrical-fixtures",
  "Rough Electrical": "rough-electrical",
  "HVAC": "hvac-final",
  "Rough Mechanical": "rough-mechanical",
  "Plumbing": "plumbing-fixtures",
  "Rough Plumbing": "rough-plumbing",
  "Crawl Space Muck": "m-and-g-crawl-space",
  "Crawl Space Insulation": "insulate-crawl-space",
  "Insulation": "insulation",
  "Drywall Hanging": "drywall-hanging",
  "Mud & Tape": "mud-and-tape",
  "Bathroom Rebuild": "bathroom-rebuild",
  "Cabinet Install": "install-cabinets",
  "Counter Install": "install-counters",
  "Appliance Install": "install-appliances",
  "Interior Paint": "interior-paint",
  "Interior Doors": "install-interior-doors",
  "Trim Install": "trim-install",
  "Flooring Install": "flooring-install",
  "Exterior Door Install": "install-exterior-doors",
  "Shutter Install": "shutter-install",
  "Driveway Repair": "repair-driveway",
  "Miscellaneous": "misc",
}

function buildReportPayload(request: PartnerReportRequest) {
  return {
    regionId: request.regionId,
    organizationName: request.organizationName,
    workScopeSlugs: request.filters.workscopes.map((label) => {
      const slug = WORK_SCOPE_SLUGS[label]
      if (!slug) throw new Error(`Unsupported work scope filter: ${label}`)
      return slug
    }),
    dateFrom: request.filters.dateCompletedFrom || undefined,
    dateTo: request.filters.dateCompletedTo || undefined,
  }
}

async function getResponseError(response: Response, fallback: string): Promise<string> {
  const raw = await response.text()
  if (!raw) return fallback
  try {
    const parsed = JSON.parse(raw) as { message?: string | string[] }
    if (Array.isArray(parsed.message)) return parsed.message.join(", ")
    return parsed.message || fallback
  } catch {
    return raw
  }
}

export async function fetchPartnerReportPdf(request: PartnerReportRequest): Promise<Blob> {
  const response = await authFetch("/reports/partner-materials/pdf", {
    method: "POST",
    body: JSON.stringify(buildReportPayload(request)),
  })

  if (!response.ok) {
    throw new Error(await getResponseError(response, "Unable to generate the partner report."))
  }

  const blob = await response.blob()
  if (!blob.size) throw new Error("The generated partner report was empty.")
  return blob
}

export async function emailPartnerReport(request: EmailPartnerReportRequest): Promise<void> {
  const response = await authFetch("/reports/partner-materials/email", {
    method: "POST",
    body: JSON.stringify({
      ...buildReportPayload(request),
      recipients: request.recipients,
      message: request.message,
    }),
  })

  if (!response.ok) {
    throw new Error(await getResponseError(response, "Unable to email the partner report."))
  }
}

